import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Modal BLOQUEANTE inicial de Nota Fiscal.
 * - Lista pendências SEM ciência → exige clique "Estou Ciente" em cada uma
 * - Após dar ciência em todas, modal fecha automaticamente
 * - Para anexar NF, montador vai para /montador/notas-fiscais (link no fim do modal)
 * - A ciência registra IP/dispositivo/timestamp em log jurídico imutável
 */
export default function AlertaNFBloqueante() {
  const [data, setData] = useState({ bloqueia_acesso: false, alertas: [] });
  const [enviando, setEnviando] = useState({}); // {pid: bool}
  const [adiado, setAdiado] = useState(!!sessionStorage.getItem("nf_bloqueante_adiado"));

  const load = async () => {
    try {
      const r = await api.get("/montador/alertas-bloqueantes");
      setData(r.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const adiar = () => {
    sessionStorage.setItem("nf_bloqueante_adiado", "1");
    setAdiado(true);
  };

  const darCiencia = async (pid) => {
    setEnviando(prev => ({ ...prev, [pid]: true }));
    try {
      await api.post(`/montador/pendencias-nf/${pid}/ciencia`);
      toast.success("Ciência registrada (auditada juridicamente)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEnviando(prev => ({ ...prev, [pid]: false })); }
  };

  if (adiado || !data.bloqueia_acesso || data.alertas.length === 0) return null;

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) adiar(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#E60000]">⚠️ Atenção · Pendência Fiscal Obrigatória</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border-l-4 border-[#FFCC00] bg-[#FFCC00]/10 p-4 text-sm">
            Você possui <strong>{data.alertas.length}</strong> pendência(s) de Nota Fiscal aguardando ciência.
            Para acessar seu painel, é necessário confirmar a ciência de cada uma.
            <br/>
            <span className="text-xs text-muted-foreground">
              Após dar ciência, você poderá anexar a NF a qualquer momento em <strong>Minhas Notas Fiscais</strong>.
            </span>
          </div>

          {data.alertas.map(a => (
            <div key={a.id} className="border-2 border-border bg-white p-5" data-testid={`alerta-nf-${a.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="label-cap text-[#142766]">{a.empresa_nome}</div>
                  <div className="font-heading font-extrabold text-xl mt-1">Período {a.periodo}</div>
                  <div className="text-sm text-muted-foreground">
                    Valor a faturar: <strong className="font-mono text-foreground">R$ {a.valor?.toFixed(2)}</strong>
                  </div>
                  <div className="text-xs text-red-700 mt-1">
                    Vencimento: <strong>{new Date(a.vencimento).toLocaleDateString("pt-BR")}</strong>
                    {a.status === "atrasada" && <span className="ml-2 px-2 py-0.5 bg-[#E60000] text-white text-[10px] uppercase">ATRASADA</span>}
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-3 text-xs text-muted-foreground space-y-1">
                <p>
                  <strong>Atenção:</strong> a emissão da Nota Fiscal é responsabilidade do Montador como prestador autônomo.
                  As informações de valor e período são fornecidas pela Empresa contratante, que é responsável por sua exatidão.
                </p>
                <p>
                  Caso a NF não seja anexada até o vencimento, o valor será <strong>acumulado para o próximo período</strong>.
                </p>
              </div>

              <Button onClick={() => darCiencia(a.id)} disabled={!!enviando[a.id]}
                className="w-full mt-4 h-12 btn-sharp brand-bg text-white" data-testid={`btn-ciencia-${a.id}`}>
                {enviando[a.id] ? "Registrando…" : "✓ Estou Ciente"}
              </Button>
            </div>
          ))}
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full gap-3">
            <div className="text-[10px] text-muted-foreground max-w-[50%]">
              Sua ciência registra IP, dispositivo e horário no log jurídico imutável.
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={adiar} className="btn-sharp" data-testid="nf-bloqueante-depois">
                Lembrar depois
              </Button>
              <Link to="/montador/notas-fiscais" className="text-sm underline text-[#142766]"
                data-testid="link-minhas-nf">
                Ver minhas NFs →
              </Link>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
